import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.js";
import { useProfile } from "../../hooks/useProfile";
import { ThemeContext } from "../../constant/theme.js";
import { LanguageContext } from "../../constant/LanguageContext.js"; // ✅ استورد LanguageContext
import { useModal } from '../../constant/ModalContext.jsx';

// Constants
import translations from "./constants/User_translations.js";
import { getUserNavItems, getAdminNavItems, pageToModuleMap, clientModulePageOverrides } from "./constants/navItems.js";
import { isAnyClientModuleEnabled } from "../../components/ClientModuleGuard.jsx";

// Hooks
import useUnreadMessages from "./hooks/useUnreadMessages.js";
import useLocation from "./hooks/useLocation.js";
import useModulePermissions from "./hooks/useModulePermissions.js";

// Components
import ThemeToggle from "./components/ThemeToggle.jsx";
import BackgroundDecor from "./components/BackgroundDecor.jsx";

// Routes
import UserRoutes from "./routes/UserRoutes.jsx";
import AdminRoutes from "./routes/AdminRoutes.jsx";

// Styles
import { buildContainerStyles, buildGlobalStyles } from "./styles/globalStyles.js";

// Other imports
import { mo3tiThemes } from "../../components/mo3tiThemes.js";
import SidebarWithTopbar from "../(All) Sidebar/SidebarWithTopbar";
import ViewTypeSwitcher from '../../components/ViewTypeSwitcher';

const Mo3tiUserProfile = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext); // âœ… Ø§Ø­ØµÙ„ Ø¹Ù„Ù‰ Ø§Ù„Ù„ØºØ©
  const isRTL = language === 'ar'; // âœ… Ø­Ø¯Ø¯ Ø§ØªØ¬Ø§Ù‡ Ø§Ù„ØµÙØ­Ø©
  const t = translations[language]; // âœ… Ø§Ø­ØµÙ„ Ø¹Ù„Ù‰ Ø§Ù„ØªØ±Ø¬Ù…Ø§Øª
  const { showModal } = useModal();

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('viewType') || 'user';
  });

  // This shell is dual-mode (UserRoutes vs AdminRoutes below) but only reads
  // viewType once on mount — react to programmatic view switches too (e.g. a
  // notification click that belongs to the other view fires
  // 'managerViewTypeChanged' after updating storage).
  useEffect(() => {
    const syncViewMode = () => {
      setViewMode(localStorage.getItem('viewType') || 'user');
    };
    window.addEventListener('managerViewTypeChanged', syncViewMode);
    window.addEventListener('storage', syncViewMode);
    return () => {
      window.removeEventListener('managerViewTypeChanged', syncViewMode);
      window.removeEventListener('storage', syncViewMode);
    };
  }, []);

  const [currentPage, setCurrentPage] = useState(() => {
    return viewMode === 'user' ? 'professionalProfile' : 'dashboard';
  });

  // Custom hooks
  const { unreadMessages } = useUnreadMessages(BASE_URL);
  const { userLocation, locationError, loading } = useLocation(t);
  const { loadingPermissions } = useModulePermissions(BASE_URL);
  const { getPageState, isClientModuleEnabled } = useAuth();

  // Check if user has an admin level assigned
  const hasAdminLevel = () => {
    try {
      const adminLevelData = localStorage.getItem('adminLevel');
      if (adminLevelData) {
        const adminLevel = JSON.parse(adminLevelData);
        return adminLevel && (adminLevel.modules?.length > 0 || adminLevel.adminLevelId);
      }
    } catch (e) {
      console.error('Error checking admin level:', e);
    }
    return false;
  };

  // Toggle between admin and manager view
  const toggleViewMode = () => {
    if (!hasAdminLevel()) {
      console.warn('User does not have admin level assigned');
      return;
    }
    const newMode = viewMode === 'admin' ? 'manager' : 'admin';
    setViewMode(newMode);
    localStorage.setItem('userViewMode', newMode);

    // Navigate based on view mode
    if (newMode === 'admin') {
      // Navigate to admin configuration page
      navigate('/admin-level-configuration');
    } else {
      // Navigate back to user profile
      navigate('/userprofile/professionalProfile');
    }
  };


  // ============ DATA CONFIGURATION ============
  const currentTheme = isDarkMode ? mo3tiThemes.dark : mo3tiThemes.light;
  // Messages has exactly one per-client switch now ("Private Messaging" on
  // the superadmin page, CLIENT_MODULE_FLAGS.messages); the old umbrella
  // "Communicator Module" toggle was folded into it by migration.
  const isPrivateMessagingEnabled = isClientModuleEnabled("messages");

  // Navigation menu items based on view
  const userNavItems = getUserNavItems(t, isPrivateMessagingEnabled, unreadMessages);
  const adminNavItems = getAdminNavItems(t, isPrivateMessagingEnabled);

  // Select nav items based on viewMode
  const allNavItems = viewMode === 'user' ? userNavItems : adminNavItems;  // Changed from 'manager'

  useEffect(() => {
    // Extract the last part of the URL path to determine the active page
    const pathParts = routerLocation.pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];

    if (lastPart) {
      // Find a matching nav item for the current path
      const matchedNavItem = allNavItems.find(
        (item) => item.page.toLowerCase() === lastPart.toLowerCase()
      );

      if (matchedNavItem) {
        setCurrentPage(matchedNavItem.page);
      } else {
        // If the path doesn't match a known nav item for this view,
        // or if it's a root path, fallback to the default page.
        setCurrentPage(viewMode === 'user' ? 'professionalProfile' : 'dashboard');
      }
    } else {
      setCurrentPage(viewMode === 'user' ? 'professionalProfile' : 'dashboard');
    }
  }, [routerLocation.pathname, viewMode, allNavItems]);

  // Filter navigation items based on admin level permissions.
  // Three-state (v2): HIDDEN removes the item, VISIBLE_ACCESS_DENIED keeps it
  // visible with a lock suffix (still clickable), VISIBLE_ACCESSIBLE is normal.
  const navItems = allNavItems
    .filter(item => {
      // Per-client module switch (superadmin): a module that is off for this
      // client is hidden fully in EVERY view (user, manager, admin), before
      // any admin-level page state is consulted.
      const clientModule = clientModulePageOverrides[item.page] || pageToModuleMap[item.page];
      if (!isAnyClientModuleEnabled(isClientModuleEnabled, clientModule)) return false;

      // Always visible items in both views
      if (viewMode === 'manager') {
        // Manager view: always show profile and settings, hide admin-only pages
        if (item.page === "professionalProfile" || item.page === "settings") {
          return true;
        }
        return true;
      }

      // Admin view: filter based on admin level permissions
      if (viewMode === 'admin') {
        // Dashboard and Settings always visible
        if (item.page === "dashboard" || item.page === "Settings") {
          return true;
        }

        // Get the required module for this page
        const requiredModule = pageToModuleMap[item.page];

        // If no module mapping exists, show the item (backward compatibility)
        if (!requiredModule) return true;

        // Show the item unless the page is HIDDEN for this user
        return getPageState(requiredModule) !== 'HIDDEN';
      }

      return true;
    })
    .map(item => {
      if (viewMode !== 'admin') return item;
      const requiredModule = pageToModuleMap[item.page];
      if (!requiredModule) return item;
      return getPageState(requiredModule) === 'VISIBLE_ACCESS_DENIED'
        ? { ...item, accessDenied: true }
        : item;
    });

  // The signed-in employee, from the server-confirmed profile only (this
  // shell renders once SessionGate has it). The pages read the identifiers
  // below for their requests (e.g. Documents, Leave), so nothing here may be
  // a placeholder — a missing value stays missing.
  const employeeData = {
    id: profile?.infinityCode || null,
    infinityCode: profile?.infinityCode || null,
    sr: profile?.employeeName || null,
    name: profile?.employeeName || null,
    email: profile?.employeeEmail || null,
    employeeId: profile?.clientCode || profile?.infinityCode || null,
    firstLineManagerInfinityCode: profile?.firstLineManagerInfinityCode || null,
    firstLevelManager: profile?.firstLineManagerInfinityCode || null,
  };

  // ============ EVENT HANDLERS ============
  const handleViewTypeChange = (type) => {
    // This shell renders BOTH the User view (UserRoutes) and the Admin view
    // (AdminRoutes) in place for the same /userprofile/* route (see the
    // viewMode checks below) - a real "user"-role account can never reach
    // /userprofilemanager or /userprofileadmin (PrivateRoute gates those to
    // manager/admin accounts only), so navigating there always got bounced
    // back - leaving viewMode/localStorage set to a view that was never
    // actually granted, which is what caused the switcher and the rendered
    // content to disagree. "user" and "admin" only ever need a local state
    // change; there is no "manager" view reachable from this shell.
    setViewMode(type);
    localStorage.setItem('viewType', type);
  };

  const handleLogout = () => {
    try {
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      // "/dashboard" alone lands the shell with no page segment selected —
      // target the actual "dashboard" page, matching how every other
      // "go to admin dashboard" navigation in the app does it.
      navigate("/dashboard/dashboard");
    } catch (error) {
      console.error("switch error:", error);
      navigate("/userprofile");
    }
  };

  const handlePageChange = (page) => {
    const navItem = navItems.find(item => item.page === page);
    if (navItem?.locked) {
      const title = (t && t.notice) || 'Notice';
      const message = navItem.lockMessage || undefined; // allow ModalContext to use localized default
      showModal({ title, message, options: { requestAccessEndpoint: process.env.REACT_APP_BASE_URL ? `${process.env.REACT_APP_BASE_URL}/api/request-module-access` : null, module: navItem.page } });
      return;
    }
    setCurrentPage(page);
    navigate(`/userprofile/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  // ============ CREATE PAGE PROPS ============
  const pageProps = {
    employeeData,
    theme: currentTheme,
    isDarkMode
  };

  // ============ STYLES ============
  const containerStyles = buildContainerStyles(currentTheme, isRTL);
  const globalStyles = buildGlobalStyles(currentTheme, isRTL, sidebarOpen);

  if (loadingPermissions) {
    return (
      <div style={{ ...containerStyles, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: 'white', fontSize: '18px' }}>Loading permissions...</div>
      </div>
    );
  }

  // ============ MAIN RENDER ============
  return (
    <div style={containerStyles}>
      {/* Background Elements */}
      <BackgroundDecor currentTheme={currentTheme} />

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={<ThemeToggle theme={theme} toggleTheme={toggleTheme} />}
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewMode}
            onSelect={handleViewTypeChange}  // Use new handler
            hasAdminLevel={hasAdminLevel}
            role={localStorage.getItem('role')}
          />
        }
      >
        <div style={{
          padding: "0",
          // Subtract the top margin so the wrapper never overflows the
          // scrolling content area (which caused a phantom scrollbar).
          height: currentPage === "messages" ? "100%" : "calc(100% - 40px)",
          display: "flex",
          flexDirection: "column",
          marginTop: currentPage === "messages" ? 0 : 40,
        }}>
          {viewMode === 'user' && (
            <UserRoutes
              key={viewMode}
              pageProps={pageProps}
              isPrivateMessagingEnabled={isPrivateMessagingEnabled}
              toggleTheme={toggleTheme}
              userLocation={userLocation}
              locationError={locationError}
              t={t}
            />
          )}

          {viewMode === 'admin' && (
            <AdminRoutes
              key={viewMode}
              pageProps={pageProps}
              isPrivateMessagingEnabled={isPrivateMessagingEnabled}
              toggleTheme={toggleTheme}
            />
          )}
        </div>
      </SidebarWithTopbar>

      {/* Global Styles */}
      <style>{globalStyles}</style>
    </div>
  );
};

export default Mo3tiUserProfile;